// ===== BEGIN FILE: src/utils/parashariAspects.ts =====
/**
 * Parashari aspects computation - single source of truth for all aspect calculations
 * This ensures consistency across Calculator tab, charts, and all other components
 */

export type PlanetName =
  | 'Sun' | 'Moon' | 'Mercury' | 'Venus' | 'Mars' | 'Jupiter' | 'Saturn' | 'Rahu' | 'Ketu';

export type HousePlacement = { house: number; offsetDeg: number };
export type PlanetsInHouses = Record<PlanetName, HousePlacement>;

export type ParashariAspect = {
  from: PlanetName;
  toHouse: number;
  kind: '3rd' | '4th' | '5th' | '7th' | '8th' | '9th' | '10th';
  fromHouse: number;
};

/** Drishti house differences for each planet (Parashari) */
const DRISHTI_HOUSE_DIFF: Record<PlanetName, number[]> = {
  Sun: [7],
  Moon: [7],
  Mercury: [7],
  Venus: [7],
  Mars: [4, 7, 8],      // 4th, 7th, 8th aspects
  Jupiter: [5, 7, 9],   // 5th, 7th, 9th aspects
  Saturn: [3, 7, 10],   // 3rd, 7th, 10th aspects
  Rahu: [5, 7, 9],      // Same as Jupiter
  Ketu: [5, 7, 9],      // Same as Jupiter
};

const DIFF_TO_NAME: Record<number, '3rd' | '4th' | '5th' | '7th' | '8th' | '9th' | '10th'> = {
  3: '3rd',
  4: '4th',
  5: '5th',
  7: '7th',
  8: '8th',
  9: '9th',
  10: '10th',
};

/** Helper: add house offset (wraps 1-12) */
function addHouse(h1: number, diff: number): number {
  return ((h1 - 1 + (diff - 1)) % 12) + 1;
}

/**
 * Compute ALL Parashari house-based aspects for given placements
 * This is the canonical function - use this everywhere for consistency
 * 
 * @param placements - Planet positions in houses
 * @param includeNodeSpecialAspects - Whether to include 5th/9th aspects for Rahu/Ketu (default: true)
 * @returns Array of all aspects (including aspects to empty houses)
 */
export function computeAllParashariAspects(
  placements: Partial<PlanetsInHouses>,
  includeNodeSpecialAspects: boolean = true
): ParashariAspect[] {
  const aspects: ParashariAspect[] = [];
  const planets = Object.keys(placements) as PlanetName[];

  planets.forEach((planet) => {
    const placement = placements[planet];
    if (!placement || !placement.house) return;

    const fromHouse = placement.house;
    let diffs = DRISHTI_HOUSE_DIFF[planet] || [];

    // Optionally exclude special aspects for nodes
    if (!includeNodeSpecialAspects && (planet === 'Rahu' || planet === 'Ketu')) {
      diffs = [7]; // Only 7th aspect
    }

    diffs.forEach((diff) => {
      const toHouse = addHouse(fromHouse, diff);
      const kind = DIFF_TO_NAME[diff];
      if (kind) {
        aspects.push({
          from: planet,
          toHouse,
          kind,
          fromHouse,
        });
      }
    });
  });

  return aspects;
}

/**
 * Get all planets aspecting a specific house
 * 
 * @param house - House number (1-12)
 * @param placements - Planet positions
 * @param includeNodeSpecialAspects - Whether to include special aspects for Rahu/Ketu
 * @returns Array of planets aspecting this house with aspect details
 */
export function getPlanetsAspectingHouse(
  house: number,
  placements: Partial<PlanetsInHouses>,
  includeNodeSpecialAspects: boolean = true
): ParashariAspect[] {
  const allAspects = computeAllParashariAspects(placements, includeNodeSpecialAspects);
  return allAspects.filter(aspect => aspect.toHouse === house);
}

/**
 * Get all houses aspected by a specific planet
 * 
 * @param planet - Planet name
 * @param placements - Planet positions
 * @param includeNodeSpecialAspects - Whether to include special aspects for Rahu/Ketu
 * @returns Array of houses aspected by this planet with aspect details
 */
export function getHousesAspectedByPlanet(
  planet: PlanetName,
  placements: Partial<PlanetsInHouses>,
  includeNodeSpecialAspects: boolean = true
): ParashariAspect[] {
  const allAspects = computeAllParashariAspects(placements, includeNodeSpecialAspects);
  return allAspects.filter(aspect => aspect.from === planet);
}

/**
 * Check if a planet aspects a specific house
 * 
 * @param planet - Planet name
 * @param house - House number (1-12)
 * @param placements - Planet positions
 * @param includeNodeSpecialAspects - Whether to include special aspects for Rahu/Ketu
 * @returns Aspect info if planet aspects the house, null otherwise
 */
export function doesPlanetAspectHouse(
  planet: PlanetName,
  house: number,
  placements: Partial<PlanetsInHouses>,
  includeNodeSpecialAspects: boolean = true
): ParashariAspect | null {
  const aspects = getHousesAspectedByPlanet(planet, placements, includeNodeSpecialAspects);
  return aspects.find(aspect => aspect.toHouse === house) || null;
}

/**
 * Format aspects for display (similar to Calculator tab format)
 * 
 * @param aspects - Array of aspects to format
 * @returns Array of formatted strings
 */
export function formatAspectsForDisplay(aspects: ParashariAspect[]): string[] {
  return aspects.map(
    a => `${a.from} -> House ${a.toHouse}: ${a.kind} aspect (H${a.fromHouse} -> H${a.toHouse})`
  ).sort();
}

// ===== END FILE: src/utils/parashariAspects.ts =====
